import React, { useCallback, useContext, useMemo } from 'react'
import moment from 'moment'
import config from 'constants/config'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { max } from 'lib/array/arrayUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { generateRoomOccupants } from 'lib/offer/occupancyUtils'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import { isFlexibleCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import ModalContext from 'contexts/ModalContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import { getBnblPackageViews, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import Pill from 'components/Luxkit/Pill'
import Group from 'components/utils/Group'
import TextLink from 'components/Luxkit/TextLink'
import TextButton from 'components/Luxkit/Button/TextButton'
import Clickable from 'components/Common/Clickable/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LineClockDollarIcon from 'components/Luxkit/Icons/line/LineClockDollarIcon'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import ReserveForZeroPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroPolicyModal'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import ReserveForZeroAndDepositPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroAndDepositPolicyModal'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import AccommodationMobileViewOptionsButton from 'components/OfferPage/Accommodation/AccommodationMobileViewOptionsButton'
import OfferPageBNBLcta from 'components/OfferPage/OfferPageBNBLcta'
import { InOfferFlowContext } from './RoomOptionsContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import FlashHotelRoomDetailsModal from './Modals/FlashHotelRoomDetailsModal'
import useLoyaltyOfferPageTargetRoomUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetRoomUpgrade'

interface Props {
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  flightOrigin?: App.Airport;
  rooms?: Array<App.Occupants>;
  checkIn?: string;
  fetching?: boolean;
  currency: string;
  isSpoofed: boolean;
  defaultDepositAmountPercentage: number;
  detailsModalMode: 'room' | 'room-and-package';
  isFlightsSelected?: boolean;
}

const defaultRooms = [generateRoomOccupants()]

function FlashHotelCheapestOptionPriceDetailsVariant(props: Props) {
  const {
    offer,
    flightOrigin,
    rooms = defaultRooms,
    checkIn,
    fetching,
    currency,
    isSpoofed,
    defaultDepositAmountPercentage,
    detailsModalMode,
    isFlightsSelected,
    packageView,
  } = props

  const totalPackageView = props.packageView
  const dispatch = useOfferPageDispatch()

  const {
    package: pkg,
    flightPrice,
    hotelValue,
    hotelDiscountPercent,
    bundledDiscountPercent,
    flightsAvailable,
    hotelPrice,
    hotelTotal,
    hotelMemberTotal,
  } = packageView

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && (flightsAvailable || offer.bundledWithFlightsOnly) && config.businessTraveller.currentAccountMode !== 'business'
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])

  const showFlightPrice = isFlightsSelected && flightsEnabled && hasFlights && !offer.bundledWithFlightsOnly

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const discountPercent = offer.bundledWithFlightsOnly ? bundledDiscountPercent : max([hotelDiscountPercent, bundledDiscountPercent])

  const totalPlusFlightsPrice = hotelTotal + flightPrice

  const { selectedTargetUpgradePackage } = useLoyaltyOfferPageTargetRoomUpgrade(pkg)
  const showBNBL = !selectedTargetUpgradePackage && packageView.capacityValid && offer.hasBuyNowBookLater && pkg.allowBuyNowBookLater && config.accommodation.hasBnbl
  const hasFlightWithCachePrice = !!flightPrice && !offer.flightCacheDisabled
  const shouldShowDiscount = !offer.isDiscountPillHidden && (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && (offer.bundledWithFlightsOnly ? hasFlightWithCachePrice : true)
  const shouldShowValue = hotelValue > hotelPrice && pkg.shouldDisplayValue
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, hotelPrice) && !!config.DEPOSITS_TAGS_ENABLED
  const isFlexibleCancellation = isFlexibleCancellationPolicy(packageView.package?.roomRate?.cancellationPolicy)

  const roomRateIsReservableForZero = isPackageEligibleForZeroReservation(offer, packageView.package.roomRate?.isReservableForZero, checkInMoment)
  const showReserveForZero = isSpoofed && roomRateIsReservableForZero
  const showDepositTag = isDepositEnabled && !isSpoofed
  const showFlexiblePaymentTag = isDepositEnabled && isSpoofed

  const showModal = useContext(ModalContext)
  const withPackageOptions = useContext(InOfferFlowContext)
  const roomModalCta = withPackageOptions ? 'View details' : 'Room details'

  const onFlexPaymentPolicyOpen = useCallback(async() => {
    showModal(
      <ReserveForZeroAndDepositPolicyModal
        depositAmountPercentage={depositAmountPercentage}
        offerType={offer.type}
        depositThresholds={offer.depositThresholds}
        depositAmount={offer.depositAmount}
      />,
    )
  }, [depositAmountPercentage, offer.depositAmount, offer.depositThresholds, offer.type, showModal])

  const openReserveForZeroPolicyModal = useCallback(() => {
    showModal(<ReserveForZeroPolicyModal />)
  }, [showModal])

  const onShowRoomDetails = useCallback(() => {
    if (detailsModalMode === 'room-and-package') {
      dispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'roomDetails' })
    } else {
      showModal(<FlashHotelRoomDetailsModal room={pkg.roomType} />)
    }
  }, [detailsModalMode, dispatch, pkg.roomType, showModal])

  return <>
    <Group direction="horizontal" gap={4} wrap="wrap" noDisplayWhenEmpty>
      {showDepositTag && <SecureWithDepositTagAndModal
        isFlexibleCancellation={isFlexibleCancellation}
        offerType={offer.type}
        depositAmountPercentage={depositAmountPercentage}
        depositThresholds={offer.depositThresholds}
      />}
      {showFlexiblePaymentTag && <Clickable onClick={onFlexPaymentPolicyOpen}>
        <Pill startIcon={<LineClockDollarIcon />} kind="tertiary" variant="primary">
          Flexible payment options
        </Pill>
      </Clickable>}
    </Group>
    <Group direction="vertical" verticalAlign="space-between" data-testid="pricing" gap={16}>
      <VerticalSpacer gap={8}>
        <VerticalSpacer gap={4}>
          <div>
            {!offer.bundledWithFlightsOnly && <>
              <PriceRowPriceCaption>
                {pluralizeToString('night', pkg.duration)} {showFlightPrice && '+ flights'} from
              </PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="L"
                price={showFlightPrice ? totalPlusFlightsPrice : hotelTotal}
                memberPrice={showFlightPrice ? hotelMemberTotal + flightPrice : hotelMemberTotal}
                saleUnit="total"
                testid="offer-package-price"
                offerConfig={offer.luxPlus}
                leadWithLuxPlusPrice
              />
              {showIncludedGuestsExceeded && <AdditionalGuestsPopup
                complex={false}
                description="+ Extra guest surcharge"
                modalContent={pkg.roomPolicyDescription}
              />}
              {shouldShowValue && <PriceRowValueDiscountWithCaption
                data-testid="value-box"
                size="M"
                originalValue={showFlightPrice ? hotelValue + flightPrice : hotelValue}
                discountPercentage={shouldShowDiscount ? discountPercent : undefined}
              />}
            </>}
            {offer.bundledWithFlightsOnly && <>
              <PriceRowPriceCaption>
                {pluralizeToString('night', pkg.duration)} + flights {!checkIn && 'from'}
              </PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="L"
                price={totalPlusFlightsPrice}
                memberPrice={hotelMemberTotal + flightPrice}
                saleUnit="total*"
                testid="offer-package-price-with-flight"
                offerConfig={offer.luxPlus}
                leadWithLuxPlusPrice
              />
              {showIncludedGuestsExceeded && <AdditionalGuestsPopup
                complex={false}
                description="+ Extra guest surcharge"
                modalContent={pkg.roomPolicyDescription}
              />}
              {shouldShowValue && <PriceRowValueDiscountWithCaption
                data-testid="value-box"
                size="M"
                originalValue={hotelValue + flightPrice}
                discountPercentage={shouldShowDiscount ? discountPercent : undefined}
              />}
            </>}
            <PriceRowTaxesAndFees />
            {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
              type="exact"
              offer={offer}
              totalCost={hotelTotal}
              numberOfNights={pkg.duration}
              roomCount={rooms?.length}
            />}
          </div>
          <PriceRowAgentHubCommission
            offerId={offer.id}
            size="M"
            productType={offer.productType}
            kind="plain"
            packageId={pkg.id}
          />
          {showReserveForZero && <TextLink
            variant="highlight"
            size="small"
            weight="bold"
            endIcon={<LineInfoCircleIcon colour="primary" />}
            onClick={openReserveForZeroPolicyModal}
          >
            Reserve now for free
          </TextLink>}
        </VerticalSpacer>
      </VerticalSpacer>
      <Group direction="horizontal" horizontalAlign="stretch" gap={8}>
        <TextButton kind="secondary" size="large" onClick={onShowRoomDetails}>
          {roomModalCta}
        </TextButton>
        <AccommodationMobileViewOptionsButton size="large" disabled={fetching} roomType={pkg.roomType} />
      </Group>
      {showBNBL && <OfferPageBNBLcta packageView={totalPackageView} direction="horizontal" />}
    </Group>
    <LoadingIndicator visible={fetching} floating opaque />
  </>
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  currency: appState.geo.currentCurrency,
  rooms: state.rooms,
  offer: state.offer,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  isLoggedIn: isLoggedInSelector(appState),
  baseBnblPackageViews: getBnblPackageViews(state),
  isSpoofed: appState.auth.account.isSpoofed,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  isFlightsSelected: state.flights,
}), FlashHotelCheapestOptionPriceDetailsVariant)
